/**
 * Formulaire des conditions d'un contrat de location reçue (lot 018).
 *
 * Fonctions pures: l'état du formulaire (tout en texte, comme les champs) ↔ le
 * corps de la requête. Aucun montant n'est calculé ici.
 */

/** Modes de rémunération, dans l'ordre où on les propose. */
export const MODES_ENTRANTS = ['PERIODE', 'ROTATION', 'LITRE', 'PART_FRET', 'GERE'];

/** Une phrase par mode: ce qui est payé au propriétaire. */
export const MODE_INDICES = {
  PERIODE: 'Un fret par jour, semaine ou mois',
  ROTATION: 'Un prix pour chaque rotation',
  LITRE: 'Un prix pour chaque litre transporté',
  PART_FRET: 'Un pourcentage du fret versé au propriétaire',
  GERE: 'Le propriétaire reçoit le fret, moins ses charges, moins notre commission',
};

/** Catégories de la matrice « qui paie quoi », dans l'ordre de l'Excel. */
export const CATEGORIES_MATRICE = [
  'SALAIRE',
  'ASSURANCE',
  'FRAIS_ROUTE',
  'FRAIS_SUPP',
  'AUTRES',
  'ENTRETIEN_REPARATION',
  'PIECES_DETACHEES',
  'PIECES_ADMIN',
  'CARBURANT',
];

export const PORTEURS = ['SOCIETE', 'PROPRIETAIRE'];

export const FOURNISSEURS_CHAUFFEUR = ['SOCIETE', 'PROPRIETAIRE'];

export const UNITES_PERIODE = ['JOUR', 'SEMAINE', 'MOIS'];

/** Comment le transporteur est réglé: relevé du mois, ou état de paiement après chaque dépotage (lot 059). */
export const MODES_REGLEMENT = ['MENSUEL', 'ETAT'];

const aujourdHui = () => {
  const d = new Date();
  const mois = String(d.getMonth() + 1).padStart(2, '0');
  const jour = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mois}-${jour}`;
};

/** Matrice par défaut: la société paie tout. */
export const matriceParDefaut = () =>
  Object.fromEntries(CATEGORIES_MATRICE.map((categorie) => [categorie, 'SOCIETE']));

/** Le formulaire d'un contrat neuf. */
export const conditionsVides = () => ({
  company_id: '',
  contract_number: '',
  mode: 'PERIODE',
  period_unit: 'MOIS',
  rate_amount: '',
  included_units: '',
  overage_rate_amount: '',
  minimum_billable_units: '',
  share_percent: '',
  commission_percent: '',
  start_date: aujourdHui(),
  end_date: '',
  driver_provider: 'SOCIETE',
  payer_matrix: matriceParDefaut(),
  alert_days_before: '15',
  due_days: '',
  notes: '',
  settlement_method: 'MENSUEL',
  statement_code: '',
});

const texte = (valeur) => (valeur === null || valeur === undefined ? '' : String(valeur));

/** Le formulaire rempli depuis un contrat existant. */
export const conditionsDepuis = (contrat) => {
  const base = conditionsVides();
  if (!contrat) return base;
  return {
    ...base,
    company_id: texte(contrat.company_id),
    contract_number: texte(contrat.contract_number),
    mode: contrat.mode || base.mode,
    period_unit: contrat.period_unit || base.period_unit,
    rate_amount: texte(contrat.rate_amount),
    included_units: texte(contrat.included_units),
    overage_rate_amount: texte(contrat.overage_rate_amount),
    minimum_billable_units: texte(contrat.minimum_billable_units),
    share_percent: texte(contrat.share_percent),
    commission_percent: texte(contrat.commission_percent),
    start_date: texte(contrat.start_date),
    end_date: texte(contrat.end_date),
    driver_provider: contrat.driver_provider || base.driver_provider,
    payer_matrix: { ...base.payer_matrix, ...(contrat.payer_matrix || {}) },
    alert_days_before: texte(contrat.alert_days_before ?? 15),
    due_days: texte(contrat.due_days),
    notes: texte(contrat.notes),
    settlement_method: contrat.settlement_method || base.settlement_method,
    statement_code: texte(contrat.statement_code),
  };
};

const nombreOuNul = (valeur) => {
  if (valeur === '' || valeur === null || valeur === undefined) return null;
  const n = Number(String(valeur).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** Les champs de tarif selon le mode: ceux qui comptent, et pas les autres. */
export const champsTarifDuMode = (mode) => {
  switch (mode) {
    case 'PERIODE':
      return ['period_unit', 'rate_amount', 'included_units', 'overage_rate_amount', 'minimum_billable_units'];
    case 'ROTATION':
    case 'LITRE':
      return ['rate_amount', 'included_units', 'overage_rate_amount', 'minimum_billable_units'];
    case 'PART_FRET':
      return ['share_percent'];
    case 'GERE':
      return ['commission_percent'];
    default:
      return [];
  }
};

/**
 * Anomalies de saisie (clés → vrai), pour marquer les champs avant l'envoi.
 * Les tarifs ne sont exigés qu'à la direction, seule à les voir.
 */
export const erreursConditions = (form, { canSeeMoney = false, nouveau = true } = {}) => ({
  company_id: nouveau && !form.company_id,
  start_date: !form.start_date,
  share_percent:
    canSeeMoney &&
    form.mode === 'PART_FRET' &&
    (nombreOuNul(form.share_percent) === null ||
      nombreOuNul(form.share_percent) < 0 ||
      nombreOuNul(form.share_percent) > 100),
  commission_percent:
    canSeeMoney &&
    form.mode === 'GERE' &&
    (nombreOuNul(form.commission_percent) === null ||
      nombreOuNul(form.commission_percent) < 0 ||
      nombreOuNul(form.commission_percent) > 100),
});

/**
 * Le corps envoyé au serveur. Les champs de tarif hors du mode choisi sont
 * remis à vide (pas laissés d'un ancien mode); sans droit aux montants, aucun
 * tarif n'est envoyé (le serveur les ignorerait de toute façon).
 */
export const corpsDepuisConditions = (form, { canSeeMoney = false, nouveau = true } = {}) => {
  const corps = {
    mode: form.mode,
    start_date: form.start_date || null,
    end_date: form.end_date || null,
    driver_provider: form.driver_provider,
    payer_matrix: { ...form.payer_matrix },
    alert_days_before: nombreOuNul(form.alert_days_before),
    due_days: nombreOuNul(form.due_days),
    notes: form.notes?.trim() || null,
  };
  if (nouveau) {
    corps.company_id = form.company_id;
    if (form.contract_number?.trim()) corps.contract_number = form.contract_number.trim();
  }
  if (canSeeMoney) {
    // Le mode de règlement et le sigle sont du ressort de la direction, comme les tarifs.
    corps.settlement_method = form.settlement_method || 'MENSUEL';
    corps.statement_code = (form.statement_code || '').trim().toUpperCase() || null;
    const utiles = new Set(champsTarifDuMode(form.mode));
    corps.period_unit = utiles.has('period_unit') ? form.period_unit : null;
    ['rate_amount', 'included_units', 'overage_rate_amount', 'minimum_billable_units', 'share_percent', 'commission_percent'].forEach(
      (champ) => {
        corps[champ] = utiles.has(champ) ? nombreOuNul(form[champ]) : null;
      },
    );
  } else if (form.mode === 'PERIODE') {
    corps.period_unit = form.period_unit;
  }
  return corps;
};

/** Étiquette du tarif principal selon le mode. */
export const etiquetteTarif = (mode, unite) => {
  if (mode === 'PERIODE') {
    const parUnite = { JOUR: 'par jour', SEMAINE: 'par semaine', MOIS: 'par mois' }[unite];
    return parUnite ? `Fret ${parUnite}` : 'Fret par période';
  }
  if (mode === 'ROTATION') return 'Prix par rotation';
  if (mode === 'LITRE') return 'Prix par litre';
  return 'Tarif';
};
